import type { CompanionBridgeEvent } from "@m8t-stack/companion-bridge-contract";
import { readCompanionRelease } from "./companion-channel.js";
import { statusCompanion, type CompanionHostOptions } from "./companion-install.js";

/**
 * What the release channel offers against what is on this machine.
 *
 * The companion has no network of its own — it reaches m8t only by running
 * this CLI — so this is the only way it can learn a newer build exists. Every
 * part of the answer is allowed to be null: nothing installed, nothing
 * offered, or neither.
 */
export async function checkCompanionUpdate(
  options: CompanionHostOptions,
  deps: { readonly readRelease?: typeof readCompanionRelease } = {},
): Promise<Extract<CompanionBridgeEvent, { type: "update" }>> {
  const installed = await statusCompanion(options)
    .then((state) => (state.state === "installed" ? state.version : null))
    .catch(() => null);
  // A channel that cannot be read is not an update: saying so would put a
  // banner in front of someone whose network happened to be down.
  const release = await (deps.readRelease ?? readCompanionRelease)().catch(() => null);
  return {
    type: "update",
    installed,
    available: release?.component.version ?? null,
    severity: release === null ? null : release.severity,
  };
}

/** Whether what is offered is something other than what is installed. */
export function companionUpdateAvailable(
  update: Extract<CompanionBridgeEvent, { type: "update" }>,
): boolean {
  return update.available !== null && update.available !== update.installed;
}
